/**
 * Timebase helpers for the renderer (pure, unit-tested).
 *
 * TimeMap estimates the graph clock from frame arrivals: each frame carries the graph time it was
 * produced at and arrives a little later. The smallest (arrival − label) offset over a recent
 * window is the best estimate of the clock offset (message delays only ever add).
 *
 * FrameHistory keeps the last ~half second of AudioFrames so the renderer can pick the frame that
 * matches what the listener hears *now*, not just the newest one (latency compensation).
 */
import { FRAME_LENGTH, readFrameTime } from '../../shared/audioFrame';

export class TimeMap {
  private readonly offsets: Float64Array;
  private pos = 0;
  private count = 0;
  private minOffset = Infinity;
  private dirty = false;

  constructor(windowSize = 128) {
    this.offsets = new Float64Array(windowSize);
  }

  get ready(): boolean {
    return this.count > 0;
  }

  /** Record a frame labelled `graphTime` (s) that arrived at `arrivalMs` (absolute ms). */
  add(graphTime: number, arrivalMs: number): void {
    this.offsets[this.pos] = arrivalMs / 1000 - graphTime;
    this.pos = (this.pos + 1) % this.offsets.length;
    if (this.count < this.offsets.length) this.count++;
    this.dirty = true;
  }

  /** Graph time at absolute wall-clock ms. */
  graphTimeAt(absMs: number): number {
    if (this.dirty) {
      let m = Infinity;
      for (let i = 0; i < this.count; i++) if (this.offsets[i]! < m) m = this.offsets[i]!;
      this.minOffset = m;
      this.dirty = false;
    }
    return absMs / 1000 - this.minOffset;
  }

  reset(): void {
    this.count = 0;
    this.pos = 0;
    this.minOffset = Infinity;
  }
}

export class FrameHistory {
  readonly frames: Float32Array[];
  private readonly times: Float64Array;
  private head = -1;
  private count = 0;

  constructor(capacity = 96) {
    this.frames = Array.from({ length: capacity }, () => new Float32Array(FRAME_LENGTH));
    this.times = new Float64Array(capacity);
  }

  get size(): number {
    return this.count;
  }

  get newestTime(): number {
    return this.head < 0 ? -Infinity : this.times[this.head]!;
  }

  push(frame: Float32Array): void {
    this.head = (this.head + 1) % this.frames.length;
    this.frames[this.head]!.set(
      frame.length === FRAME_LENGTH ? frame : frame.subarray(0, FRAME_LENGTH),
    );
    this.times[this.head] = readFrameTime(frame);
    if (this.count < this.frames.length) this.count++;
  }

  /**
   * The newest frame labelled at or before `t`. If `t` is newer than everything, the newest frame;
   * if older than everything, the oldest. Returns null when empty.
   */
  select(t: number): Float32Array | null {
    if (this.count === 0) return null;
    const cap = this.frames.length;
    for (let i = 0; i < this.count; i++) {
      const idx = (this.head - i + cap) % cap;
      if (this.times[idx]! <= t) return this.frames[idx]!;
    }
    return this.frames[(this.head - this.count + 1 + cap) % cap]!;
  }

  /** Time of the most recent hit at or before `t` (searching the history), or -Infinity. */
  lastHitBefore(t: number, hitIndex: number): number {
    const cap = this.frames.length;
    for (let i = 0; i < this.count; i++) {
      const idx = (this.head - i + cap) % cap;
      const ti = this.times[idx]!;
      if (ti <= t && this.frames[idx]![hitIndex]! >= 0.5) return ti;
    }
    return -Infinity;
  }

  reset(): void {
    this.head = -1;
    this.count = 0;
  }
}
